import React, { useRef, useEffect, useState, useMemo, useCallback } from 'react';
import { KillEvent, PlayerHurtEvent, BulletImpactEvent, EntityStartEvent, GrenadeDetonateEvent, FlashBlindEvent, ParsedReplay } from '../MatchPage/Replay/replayParser';
import { useMapControl } from '../MatchPage/Replay/mapControl/useMapControl';
import { drawMapControl } from '../MatchPage/Replay/mapControl/mapControlRenderer';
import { renderFrame, mapImageMappings, mapConfigs, multiLevelMaps, KillFeedEntry, TracerEntry, GrenadeTrajectory, FlashPopEntry, DeathMarker, HitMarkerEntry, filterFrameByLevel, filterWorldByLevel, buildEntityZLookup, filterByPlayerZ, EntityZData } from '../MatchPage/Replay/replayRenderer';
import { preloadIcons, PreloadedIcons } from '../MatchPage/Replay/weaponIcons';

interface AnalysisReplayCanvasProps {
    /** show the map-control field under the round, matching the analysis view you came from */
    showMapControl?: boolean;
    replay: ParsedReplay;
    currentFrame: number;
    mapName: string;
    playerNames?: Record<string, string>;
    isBombPlanted?: boolean;
    bombPlantFrame?: number | null;
    zoom: number;
    panOffset: { x: number; y: number };
    onZoomChange: (zoom: number) => void;
    onPanOffsetChange: (offset: { x: number; y: number }) => void;
    isPanMode: boolean;
}

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;
const SPLIT_GAP = 0;

function clampOffset(x: number, y: number, zoom: number, contentW: number, contentH?: number): { x: number; y: number } {
    const h = contentH ?? contentW;
    return {
        x: Math.max(-(contentW * zoom - contentW), Math.min(0, x)),
        y: Math.max(-(h * zoom - h), Math.min(0, y)),
    };
}

const AnalysisReplayCanvas: React.FC<AnalysisReplayCanvasProps> = ({
    replay, currentFrame, mapName, showMapControl = false, playerNames = {}, isBombPlanted = false, bombPlantFrame = null,
    zoom, panOffset, onZoomChange, onPanOffsetChange, isPanMode,
}) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [mapImage, setMapImage] = useState<HTMLImageElement | null>(null);
    const [lowerMapImage, setLowerMapImage] = useState<HTMLImageElement | null>(null);
    const mlConfig = multiLevelMaps[mapName] ?? null;
    const isMultiLevel = !!mlConfig;
    const [canvasSize, setCanvasSize] = useState(800);
    const [canvasWidth, setCanvasWidth] = useState(800);
    const [canvasHeight, setCanvasHeight] = useState(800);
    const [icons, setIcons] = useState<PreloadedIcons | null>(null);
    const projPathsRef = useRef(new Map<string, Array<Array<{ x: number; y: number }>>>());

    // Map control for the inspected round. Coming here from the map-control view and finding the
    // overlay missing is jarring, so the round you open shows the same field you clicked from.
    const zSplit = mlConfig ? (mlConfig.zUpper + mlConfig.zLower) / 2 : 0;
    const upperBand = useMemo(() => (mlConfig ? { zMin: zSplit } : undefined), [mlConfig, zSplit]);
    const lowerBand = useMemo(() => (mlConfig ? { zMax: zSplit } : undefined), [mlConfig, zSplit]);
    const mapControl = useMapControl(replay, mlConfig ? `${mapName}_upper` : mapName, showMapControl, upperBand);
    const mapControlLower = useMapControl(replay, mlConfig ? `${mapName}_lower` : '', showMapControl && !!mlConfig, lowerBand);
    const projPathsUpperRef = useRef(new Map<string, Array<Array<{ x: number; y: number }>>>());
    const projPathsLowerRef = useRef(new Map<string, Array<Array<{ x: number; y: number }>>>());

    // Refs to avoid stale closures in native event listeners
    const zoomRef = useRef(zoom);
    const panOffsetRef = useRef(panOffset);
    const canvasWidthRef = useRef(canvasWidth);
    const canvasHeightRef = useRef(canvasHeight);
    useEffect(() => { zoomRef.current = zoom; }, [zoom]);
    useEffect(() => { panOffsetRef.current = panOffset; }, [panOffset]);
    useEffect(() => { canvasWidthRef.current = canvasWidth; }, [canvasWidth]);
    useEffect(() => { canvasHeightRef.current = canvasHeight; }, [canvasHeight]);

    const layoutWRef = useRef(canvasSize);
    const layoutHRef = useRef(canvasSize);
    useEffect(() => {
        if (mlConfig) {
            const s = canvasSize / 1024;
            layoutWRef.current = mlConfig.upperCrop.w * s + SPLIT_GAP + mlConfig.lowerCrop.w * s;
            layoutHRef.current = Math.max(mlConfig.upperCrop.h * s, mlConfig.lowerCrop.h * s);
        } else {
            layoutWRef.current = canvasSize;
            layoutHRef.current = canvasSize;
        }
    }, [canvasSize, mlConfig]);

    // Pan drag state (refs to avoid re-renders during drag)
    const isDragging = useRef(false);
    const dragStart = useRef({ x: 0, y: 0 });
    const panStart = useRef({ x: 0, y: 0 });
    const [isDraggingState, setIsDraggingState] = useState(false);

    useEffect(() => {
        if (mlConfig) {
            const upperSrc = mapImageMappings[mlConfig.upperImage];
            const lowerSrc = mapImageMappings[mlConfig.lowerImage];
            if (upperSrc) { const img = new Image(); img.onload = () => setMapImage(img); img.src = upperSrc; }
            if (lowerSrc) { const img = new Image(); img.onload = () => setLowerMapImage(img); img.src = lowerSrc; }
        } else {
            const src = mapImageMappings[mapName];
            if (!src) return;
            const img = new Image();
            img.onload = () => setMapImage(img);
            img.src = src;
            setLowerMapImage(null);
        }
    }, [mapName, mlConfig]);

    useEffect(() => {
        preloadIcons().then(setIcons).catch(() => {});
    }, []);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        const ro = new ResizeObserver(entries => {
            for (const entry of entries) {
                const { width, height } = entry.contentRect;
                if (width <= 0 || height <= 0) continue;
                const dpr = Math.min(window.devicePixelRatio || 1, 2);
                const w = Math.min(Math.round(width * dpr), 2048);
                const h = Math.min(Math.round(height * dpr), 2048);

                if (mlConfig) {
                    const uc = mlConfig.upperCrop;
                    const lc = mlConfig.lowerCrop;
                    const totalCropW = uc.w + lc.w;
                    const maxCropH = Math.max(uc.h, lc.h);
                    const scaleByW = (w - SPLIT_GAP) / totalCropW * 1024;
                    const scaleByH = h / maxCropH * 1024;
                    setCanvasSize(Math.min(scaleByW, scaleByH));
                } else {
                    setCanvasSize(Math.min(w, h));
                }
                setCanvasWidth(w);
                setCanvasHeight(h);
            }
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, [mlConfig]);

    // Native wheel listener (must be non-passive to call preventDefault)
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const handler = (e: WheelEvent) => {
            e.preventDefault();

            const rect = canvas.getBoundingClientRect();
            const cssToCanvasX = canvasWidthRef.current / rect.width;
            const cssToCanvasY = canvasHeightRef.current / rect.height;
            // Mouse position in canvas pixel coords, adjusted for map centering offset
            // (the layout, not canvasSize: Nuke's two panels aren't square)
            const mapOffX = (canvasWidthRef.current - layoutWRef.current) / 2;
            const mapOffY = (canvasHeightRef.current - layoutHRef.current) / 2;
            const mouseX = (e.clientX - rect.left) * cssToCanvasX - mapOffX;
            const mouseY = (e.clientY - rect.top) * cssToCanvasY - mapOffY;

            const oldZoom = zoomRef.current;
            const zoomDelta = e.deltaY > 0 ? -0.15 : 0.15;
            const newZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, oldZoom + zoomDelta));
            if (newZoom === oldZoom) return;

            if (newZoom <= 1) {
                onZoomChange(1);
                onPanOffsetChange({ x: 0, y: 0 });
            } else {
                // Cursor-centered zoom: keep the point under the cursor fixed
                const worldX = (mouseX - panOffsetRef.current.x) / oldZoom;
                const worldY = (mouseY - panOffsetRef.current.y) / oldZoom;
                const newOffsetX = mouseX - worldX * newZoom;
                const newOffsetY = mouseY - worldY * newZoom;

                onZoomChange(newZoom);
                onPanOffsetChange(clampOffset(newOffsetX, newOffsetY, newZoom, layoutWRef.current, layoutHRef.current));
            }
        };

        canvas.addEventListener('wheel', handler, { passive: false });
        return () => canvas.removeEventListener('wheel', handler);
    }, [onZoomChange, onPanOffsetChange]);

    // Pan handlers
    const dragDistance = useRef(0);

    const handleMouseDown = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
        dragDistance.current = 0;
        if (!isPanMode || zoom <= 1) return;
        isDragging.current = true;
        dragStart.current = { x: e.clientX, y: e.clientY };
        panStart.current = { x: panOffsetRef.current.x, y: panOffsetRef.current.y };
        setIsDraggingState(true);
        e.preventDefault();
    }, [isPanMode, zoom]);

    const handleMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
        if (!isDragging.current) return;
        const canvas = canvasRef.current;
        if (!canvas) return;

        const dx = e.clientX - dragStart.current.x;
        const dy = e.clientY - dragStart.current.y;
        dragDistance.current = Math.sqrt(dx * dx + dy * dy);

        const rect = canvas.getBoundingClientRect();
        const cssToCanvasX = canvasWidthRef.current / rect.width;
        const cssToCanvasY = canvasHeightRef.current / rect.height;

        const canvasDx = (e.clientX - dragStart.current.x) * cssToCanvasX;
        const canvasDy = (e.clientY - dragStart.current.y) * cssToCanvasY;

        const newX = panStart.current.x + canvasDx;
        const newY = panStart.current.y + canvasDy;

        onPanOffsetChange(clampOffset(newX, newY, zoomRef.current, layoutWRef.current, layoutHRef.current));
    }, [onPanOffsetChange]);

    const handleMouseUp = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
        const wasDragged = dragDistance.current > 5;
        if (isDragging.current) {
            isDragging.current = false;
            setIsDraggingState(false);
        }

        // No click actions in analysis replay mode
    }, [replay, currentFrame, mapName]);

    const handleMouseLeave = useCallback(() => {
        if (isDragging.current) {
            isDragging.current = false;
            setIsDraggingState(false);
        }
    }, []);

    const killFeed = useMemo<KillFeedEntry[]>(() =>
        replay.events
            .filter((e): e is KillEvent => e.type === 'kill')
            .map(k => ({
                killerSlot: k.killerSlot,
                victimSlot: k.victimSlot,
                weaponId: k.weaponId,
                headshot: k.headshot,
                wallbang: k.wallbang,
                noscope: k.noscope,
                thrusmoke: k.thrusmoke,
                frameIdx: k.frameIdx,
            })),
        [replay.events]
    );

    const tracers = useMemo<TracerEntry[]>(() => {
        const t = replay.events
            .filter((e): e is BulletImpactEvent => e.type === 'bullet_impact')
            .map(e => ({
                shooterX: e.shooterX,
                shooterY: e.shooterY,
                impactX: e.impactX,
                impactY: e.impactY,
                frameIdx: e.frameIdx,
                shooterSlot: e.shooterSlot,
            }));
        // Debug: log event counts for this round
        const eventTypes: Record<string, number> = {};
        replay.events.forEach(e => { eventTypes[e.type] = (eventTypes[e.type] || 0) + 1; });
        console.log('[ReplayDebug] Events:', eventTypes, `| Frames: ${replay.frames.length} | Tracers: ${t.length}`);
        return t;
    }, [replay.events]);

    const grenadeTrajectories = useMemo<GrenadeTrajectory[]>(() => {
        const results: GrenadeTrajectory[] = [];
        const startEvents = replay.events.filter(
            (e): e is EntityStartEvent => e.type === 'smoke_start' || e.type === 'inferno_start' || e.type === 'decoy_start'
        );
        for (const evt of startEvents) {
            // Get thrower position from the frame at the event time
            const frame = replay.frames[Math.min(evt.frameIdx, replay.frames.length - 1)];
            if (!frame || evt.throwerSlot === 0xff || evt.throwerSlot >= frame.players.length) continue;
            const thrower = frame.players[evt.throwerSlot];
            if (!thrower) continue;
            results.push({
                throwerSlot: evt.throwerSlot,
                startX: thrower.x,
                startY: thrower.y,
                endX: evt.x,
                endY: evt.y,
                frameIdx: evt.frameIdx,
                type: evt.type,
                entityId: evt.entityId,
            });
        }
        return results;
    }, [replay.events, replay.frames]);

    // Build smoke entity -> team lookup
    const smokeTeams = useMemo<Record<number, 'T' | 'CT'>>(() => {
        const map: Record<number, 'T' | 'CT'> = {};
        const smokeStarts = replay.events.filter(
            (e): e is EntityStartEvent => e.type === 'smoke_start'
        );
        for (const evt of smokeStarts) {
            if (evt.throwerSlot !== 0xff && evt.throwerSlot < replay.roster.length) {
                map[evt.entityId] = replay.roster[evt.throwerSlot].team;
            }
        }
        return map;
    }, [replay.events, replay.roster]);

    // Flash detonation locations
    const flashPops = useMemo<FlashPopEntry[]>(() =>
        replay.events
            .filter((e): e is GrenadeDetonateEvent => e.type === 'flash_detonate' || e.type === 'he_detonate')
            .map(e => ({
                x: e.x, y: e.y, frameIdx: e.frameIdx,
                grenadeType: e.type === 'flash_detonate' ? 'flash' as const : 'he' as const,
            })),
        [replay.events]
    );

    // Flash blind events for computing remaining flash duration per player
    const flashBlinds = useMemo(() =>
        replay.events
            .filter((e): e is FlashBlindEvent => e.type === 'flash_blind')
            .map(e => ({ victimSlot: e.victimSlot, frameIdx: e.frameIdx, durationSeconds: e.durationSeconds })),
        [replay.events]
    );

    // Death markers — victim position at time of death
    const deathMarkers = useMemo<DeathMarker[]>(() =>
        replay.events
            .filter((e): e is KillEvent => e.type === 'kill')
            .map(k => {
                const frame = replay.frames[Math.min(k.frameIdx, replay.frames.length - 1)];
                if (!frame || k.victimSlot >= frame.players.length) return null;
                const victim = frame.players[k.victimSlot];
                const team = replay.roster[k.victimSlot]?.team;
                if (!team) return null;
                return { x: victim.x, y: victim.y, team, frameIdx: k.frameIdx };
            })
            .filter((m): m is DeathMarker => m !== null),
        [replay.events, replay.frames, replay.roster]
    );

    // Hit markers — attacker/victim positions from player_hurt events
    const hitMarkers = useMemo<HitMarkerEntry[]>(() =>
        replay.events
            .filter((e): e is PlayerHurtEvent => e.type === 'player_hurt' && e.attackerSlot != null && e.weaponId > 0 && e.weaponId < 70)
            .map(h => {
                const frame = replay.frames[Math.min(h.frameIdx, replay.frames.length - 1)];
                if (!frame || h.attackerSlot == null) return null;
                const attacker = h.attackerSlot < frame.players.length ? frame.players[h.attackerSlot] : null;
                const victim = h.victimSlot < frame.players.length ? frame.players[h.victimSlot] : null;
                if (!attacker || !victim) return null;
                return {
                    attackerX: attacker.x, attackerY: attacker.y,
                    victimX: victim.x, victimY: victim.y,
                    attackerSlot: h.attackerSlot,
                    frameIdx: h.frameIdx,
                };
            })
            .filter((m): m is HitMarkerEntry => m !== null),
        [replay.events, replay.frames]
    );

    // Fire entity -> team lookup
    const fireTeams = useMemo<Record<number, 'T' | 'CT'>>(() => {
        const map: Record<number, 'T' | 'CT'> = {};
        replay.events
            .filter((e): e is EntityStartEvent => e.type === 'inferno_start')
            .forEach(evt => {
                if (evt.throwerSlot !== 0xff && evt.throwerSlot < replay.roster.length)
                    map[evt.entityId] = replay.roster[evt.throwerSlot].team;
            });
        return map;
    }, [replay.events, replay.roster]);

    // Fire entity -> start frame lookup
    const fireStartFrames = useMemo<Record<number, number>>(() => {
        const map: Record<number, number> = {};
        replay.events
            .filter((e): e is EntityStartEvent => e.type === 'inferno_start')
            .forEach(evt => { map[evt.entityId] = evt.frameIdx; });
        return map;
    }, [replay.events]);

    // Multi-level: entity ID -> Z lookup
    const entityZData = useMemo<EntityZData>(() => {
        if (!isMultiLevel) return { entityZ: new Map(), slotThrows: new Map() };
        return buildEntityZLookup(replay.events, replay.frames);
    }, [replay.events, replay.frames, isMultiLevel]);

    // Render with zoom/pan transform
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || !mapImage) return;
        if (isMultiLevel && !lowerMapImage) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const frame = replay.frames[currentFrame];
        if (!frame) return;

        // Clear full canvas and fill with background
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, canvasWidth, canvasHeight);
        ctx.fillStyle = '#1a1a1a';
        ctx.fillRect(0, 0, canvasWidth, canvasHeight);

        if (isMultiLevel && mlConfig && lowerMapImage) {
            const uc = mlConfig.upperCrop;
            const lc = mlConfig.lowerCrop;
            const zU = mlConfig.zUpper;
            const zL = mlConfig.zLower;
            const s = canvasSize / 1024;
            const upperPanelW = uc.w * s;
            const upperPanelH = uc.h * s;
            const lowerPanelW = lc.w * s;
            const lowerPanelH = lc.h * s;
            const totalW = upperPanelW + SPLIT_GAP + lowerPanelW;
            const offsetX = (canvasWidth - totalW) / 2;
            const offsetY = (canvasHeight - Math.max(upperPanelH, lowerPanelH)) / 2;

            const renderLevel = (
                level: 'upper' | 'lower',
                img: HTMLImageElement,
                crop: { x: number; y: number; w: number; h: number },
                panelX: number, panelW: number, panelH: number,
            ) => {
                const filteredFrame = filterFrameByLevel(frame, level, zU, zL, mlConfig.bothZone);
                const filteredWorld = filterWorldByLevel(filteredFrame.world, entityZData, frame, currentFrame, level, zU, zL);
                const levelFrame = { ...filteredFrame, world: filteredWorld };
                const levelTracers = filterByPlayerZ(tracers, replay.frames, t => t.shooterSlot, level, zU, zL);
                const levelHitMarkers = filterByPlayerZ(hitMarkers, replay.frames, h => h.attackerSlot, level, zU, zL);
                const midZ = (zU + zL) / 2;
                const levelGrenadeTrajectories = grenadeTrajectories.filter(g => {
                    const z = entityZData.entityZ.get(g.entityId);
                    if (z === undefined) return false;
                    return level === 'upper' ? z >= midZ : z < midZ;
                });
                const levelDeathMarkers = deathMarkers.filter(dm => {
                    const f = replay.frames[Math.min(dm.frameIdx, replay.frames.length - 1)];
                    if (!f) return false;
                    for (const p of f.players) {
                        if (Math.abs(p.x - dm.x) < 2 && Math.abs(p.y - dm.y) < 2)
                            return p.z >= zU ? level === 'upper' : p.z <= zL ? level === 'lower' : true;
                    }
                    return false;
                });

                ctx.save();
                ctx.beginPath();
                ctx.rect(panelX, 0, panelW, panelH);
                ctx.clip();
                ctx.translate(panelX - crop.x * s, -crop.y * s);

                const levelProjPaths = level === 'upper' ? projPathsUpperRef.current : projPathsLowerRef.current;
                renderFrame(ctx, levelFrame, replay.roster, mapName, img, canvasSize,
                    killFeed, currentFrame, playerNames, levelTracers, levelGrenadeTrajectories,
                    smokeTeams, icons, flashPops, fireTeams, fireStartFrames,
                    levelProjPaths, flashBlinds, levelDeathMarkers, levelHitMarkers,
                    isBombPlanted, bombPlantFrame,
                    null, false, new Set<number>(), [], null,
                    (() => {
                        const lc = level === 'upper' ? mapControl : mapControlLower;
                        const st = lc.data?.statesAtFrame(currentFrame) ?? null;
                        return lc.data && st
                            ? (c: CanvasRenderingContext2D, size: number) => drawMapControl(c, lc.data!.grid, lc.data!.cells, st, size, currentFrame)
                            : null;
                    })());

                ctx.translate(-(panelX - crop.x * s), crop.y * s);
                ctx.translate(panelX, 0);
                ctx.font = `bold ${Math.max(10, Math.round(canvasSize / 80))}px Arial`;
                ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'top';
                ctx.fillText(level === 'upper' ? 'UPPER' : 'LOWER', panelW / 2, 6);
                ctx.restore();
            };

            const upperPanelX = 0;
            const lowerPanelX = upperPanelW + SPLIT_GAP;

            ctx.save();
            ctx.translate(offsetX + panOffset.x, offsetY + panOffset.y);
            ctx.scale(zoom, zoom);
            renderLevel('upper', mapImage, uc, upperPanelX, upperPanelW, upperPanelH);
            renderLevel('lower', lowerMapImage, lc, lowerPanelX, lowerPanelW, lowerPanelH);
            ctx.restore();
        } else {
            const mapOffsetX = (canvasWidth - canvasSize) / 2;
            const mapOffsetY = (canvasHeight - canvasSize) / 2;
            ctx.save();
            ctx.translate(panOffset.x + mapOffsetX, panOffset.y + mapOffsetY);
            ctx.scale(zoom, zoom);

            const controlStates = mapControl.data?.statesAtFrame(currentFrame) ?? null;
            const controlUnderlay = mapControl.data && controlStates
                ? (c: CanvasRenderingContext2D, size: number) =>
                    drawMapControl(c, mapControl.data!.grid, mapControl.data!.cells, controlStates, size, currentFrame)
                : null;
            renderFrame(ctx, frame, replay.roster, mapName, mapImage, canvasSize, killFeed, currentFrame, playerNames, tracers, grenadeTrajectories, smokeTeams, icons, flashPops, fireTeams, fireStartFrames, projPathsRef.current, flashBlinds, deathMarkers, hitMarkers, isBombPlanted, bombPlantFrame, null, false, new Set<number>(), [], null, controlUnderlay);

            ctx.restore();
        }
    }, [currentFrame, mapImage, lowerMapImage, canvasSize, canvasWidth, canvasHeight, replay, mapName, icons, isBombPlanted, bombPlantFrame, zoom, panOffset, isMultiLevel, mlConfig, entityZData]);

    const cursor = isPanMode && zoom > 1
        ? (isDraggingState ? 'grabbing' : 'grab')
        : 'default';

    return (
        <div ref={containerRef} style={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', position: 'relative' }}>
            <canvas
                ref={canvasRef}
                width={canvasWidth}
                height={canvasHeight}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseLeave}
                style={{
                    display: 'block',
                    width: canvasWidth / Math.min(window.devicePixelRatio || 1, 2),
                    height: canvasHeight / Math.min(window.devicePixelRatio || 1, 2),
                    maxWidth: '100%',
                    maxHeight: '100%',
                    cursor,
                }}
            />
        </div>
    );
};

export default AnalysisReplayCanvas;
